import React, { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import InfoBanner from '@/components/molecules/info-banner';
import { useTranslations } from 'use-intl';
import Link from '@/components/atoms/link';
import { Approver } from '@/types/entity/approval-flow';
import { Group, Rule } from '@/components/organisms/rule-builder/types';
import RulePreview from '@/components/organisms/rule-builder/components/rule-preview';
import { classnames } from '@/utils/classnames/classnames';
import { CheckIcon } from '@heroicons/react/24/outline';
import Button from '@/components/atoms/button';
import TextArea from '@/components/atoms/text-area';
import toast from '@/components/atoms/toaster/helpers/toast';
import { ApprovalFlowDetailsPageProps } from './types';
import PreviousPageLink from '../../components/previous-page-link';
import ApprovalFlowStatusTag from '../../components/approval-flow-status-tag';
import { DashboardLinks } from '../../constants';
import OrderDetailsPage from '../order-details';

const ApprovalFlowDetailsPage = ({
  approvalFlow,
  userRoles,
  viewOnly,
  approversCriteria = [],
  onApprove,
  onReject,
}: ApprovalFlowDetailsPageProps) => {
  const translate = useTranslations();

  const [processing, setProcessing] = useState(false);

  const [rejectionReasonTextarea, setRejectionReasonTextarea] = useState(false);
  const maxRejectionReasonCharacters = 160;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ rejectionReason: string }>({
    defaultValues: {
      rejectionReason: '',
    },
  });

  const isInApproverTiers = useCallback((approver: Approver, tier: Group | Rule): boolean => {
    if (tier.type === 'rule') return approver.key === tier.key;

    // `isInApproverTiers` recurses on itself inside its own useCallback body; the identifier
    // can't be "declared above" a use nested within its own initializer. Safe at runtime — the
    // recursive call only fires after this render's `const` binding is fully assigned, so there's
    // no TDZ/staleness risk to reorder away. Not a genuine reorder case; logged per EWM-760's
    // P-disable fallback.
    // eslint-disable-next-line react-hooks/immutability -- EWM-760: verified false positive (see above).
    return tier.rules.some((r) => isInApproverTiers(approver, r));
  }, []);

  const mergedApprovers = useMemo<Group[]>(() => {
    if (!approvalFlow.rules) return [];

    const maxTiersLength = approvalFlow.rules.reduce((acc, curr) => Math.max(acc, curr.approvers?.length ?? 0), 0);

    const tiers = [] as Group[];

    for (let i = 0; i < maxTiersLength; ++i) {
      tiers.push({ type: 'group', combinator: 'AND', rules: [] });

      for (const approvalRule of approvalFlow.rules) {
        if (!approvalRule.approvers?.length) continue;

        if (i < approvalRule.approvers.length) (tiers.at(-1) as Group).rules.push(approvalRule.approvers[i]);
      }
    }

    return tiers;
  }, [approvalFlow.rules]);

  //Next tier index to be approved
  const currentPendingTierIndex = mergedApprovers.findIndex((rule) =>
    (approvalFlow.currentPendingApproverTiers ?? []).some((approver) => isInApproverTiers(approver, rule)),
  );

  //Next tier the current user can approve
  const currentUserPendingTierIndex = mergedApprovers.findIndex((rule) =>
    userRoles
      .filter(
        (role) => !approvalFlow.approvals.some((approval) => !!approval.approver.roles.find((r) => r.key === role.key)),
      )
      .some(
        (role) =>
          isInApproverTiers(role, rule) && (approvalFlow.eligibleApprovers ?? []).some((r) => r.key === role.key),
      ),
  );

  //Whether the user can approve current pending tier?
  const userCanApprovePendingTier = currentPendingTierIndex === currentUserPendingTierIndex;

  //Whether the user already approved the flow
  const userHasApproved = userRoles.every((role) =>
    approvalFlow.approvals.some((approval) => !!approval.approver.roles.find((r) => r.key === role.key)),
  );

  const onSubmit = handleSubmit(async (data) => {
    setProcessing(true);
    await onReject?.(data.rejectionReason);
    setRejectionReasonTextarea(false);
    setProcessing(false);

    toast.success(translate('dashboard.approval-flow-rejected'), { position: 'top-right' });
  });

  return (
    <div>
      {viewOnly && (
        <InfoBanner className="mt-3">
          <b>{translate('common.view-only')}</b> {translate('dashboard.flow-view-only-desc')}
        </InfoBanner>
      )}

      <div className="flex items-center justify-between">
        <h1 className="text-18 md:text-20 lg:text-24 py-6 font-extrabold text-gray-800 md:py-7 lg:py-9">
          {translate('dashboard.flow-details')}
        </h1>
        <div className="hidden items-center justify-normal gap-x-3 md:flex">
          <PreviousPageLink />
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <h3 className="text-14 text-gray-600">
          {translate('dashboard.flow-id')}: {approvalFlow.id}
        </h3>

        <h3 className="text-14 flex items-center gap-2 text-gray-600">
          <span>{translate('common.status')}:</span>
          <ApprovalFlowStatusTag status={approvalFlow.status} />
        </h3>
      </div>

      <div className="mt-9 border-t border-neutral-400 pt-10">
        <h5 className="text-20 pb-9 font-bold text-gray-700">{translate('common.approval-flow')}</h5>

        <div>
          <h6 className="text-16 font-semibold text-gray-700">{translate('dashboard.rules-applied')}</h6>
          <div className="mt-4 flex flex-wrap items-center gap-4 rounded-md border border-gray-300 p-6">
            {(approvalFlow.rules ?? []).map((rule, index, arr) => (
              <div key={rule.id}>
                <Link
                  href={`${DashboardLinks.approvalRules}?subPath=edit-approval-rule&id=${rule.id}`}
                  className="text-16 text-blue-700 underline"
                >
                  {rule.name}
                </Link>{' '}
                {index < arr.length - 1 && <span>,</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div>
          <h5 className="text-16 font-semibold text-gray-700">
            {translate('dashboard.approval-flow-required-approvers')}
          </h5>
          <div
            className="text-16 mt-1 font-normal text-gray-700"
            dangerouslySetInnerHTML={{
              __html: translate('dashboard.approval-flow-required-approvers-desc', {
                preview: `<div class="w-fit border border-blue-500 p-1 inline-flex items-center gap-1 rounded-sm text-xs text-blue-500">
                    <span>${translate('common.name')}</span>
                    <span class="w-[12px] h-[12px] border border-gray-500 rounded-full block" />
                  </div>`,
              }),
            }}
          />
        </div>

        <div className="mt-4 rounded-md border border-gray-300 px-7">
          {mergedApprovers.map((approver, index, arr) => (
            <div key={index} className={classnames('py-6', { 'border-b border-neutral-400': index < arr.length - 1 })}>
              <div className="flex items-start gap-5">
                <div className="text-16 shrink-0 rounded-full bg-neutral-200 px-6 py-1 font-medium text-gray-700">{`${translate('dashboard.tier')} ${index + 1}`}</div>
                <RulePreview
                  group={approver}
                  criteria={approversCriteria}
                  renderRule={({ key, name }) => {
                    const isUserRole = userRoles.some((role) => role.key === key);
                    const isRoleApproved = approvalFlow.approvals.some(
                      (approval) => !!approval.approver.roles.find((r) => r.key === key),
                    );

                    return (
                      <div
                        className={classnames('flex w-fit items-center gap-2 rounded-sm border px-2 py-1', {
                          'border-gray-300': !isUserRole,
                          'border-blue-500': isUserRole,
                        })}
                      >
                        <span
                          className={classnames({
                            'text-gray-600': !isUserRole,
                            'text-blue-500': isUserRole,
                          })}
                        >
                          {name}
                        </span>
                        <span
                          className={classnames(
                            'flex size-[20px] items-center justify-center rounded-full border-2 p-[3px]',
                            {
                              'border-green-600': isRoleApproved,
                              'border-gray-300': !isRoleApproved,
                            },
                          )}
                        >
                          {isRoleApproved && <CheckIcon className="stroke-[5px] text-green-600" />}
                        </span>
                      </div>
                    );
                  }}
                />
              </div>
              <div className="flex flex-wrap items-center pt-6">
                {(approvalFlow.rules ?? []).map((rule, index, arr) => (
                  <div key={rule.id} className="whitespace-pre text-gray-500">
                    {rule.name} {index < arr.length - 1 && <span>,{'  '}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-7 flex gap-3">
          {rejectionReasonTextarea ? (
            <form className="w-full" onSubmit={onSubmit}>
              <h6 className="text-12 mb-3 font-medium text-gray-600 uppercase">
                {translate('dashboard.approval-flow-rejection-reason')}
              </h6>

              <TextArea
                {...register('rejectionReason', {
                  maxLength: {
                    value: maxRejectionReasonCharacters,
                    message: translate('dashboard.message-too-long', {
                      maxCharacters: maxRejectionReasonCharacters.toString(),
                    }),
                  },
                })}
                className="h-[150px] w-full md:h-[65px]"
                fitContent={false}
                error={errors.rejectionReason?.message}
              />

              <div className="mt-6 flex gap-3">
                <Button variant="secondary" size="l" onClick={() => setRejectionReasonTextarea(false)}>
                  {translate('common.cancel')}
                </Button>
                <Button type="submit" size="l" variant="primary" loading={processing}>
                  {translate('common.reject')}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <Button
                variant="secondary"
                size="l"
                className="leading-[16px]"
                disabled={
                  viewOnly || userHasApproved || !userCanApprovePendingTier || approvalFlow.status !== 'pending'
                }
                onClick={() => {
                  setRejectionReasonTextarea(true);
                }}
              >
                {translate('common.reject')}
              </Button>
              <Button
                variant="primary"
                size="l"
                className="py-[12px] leading-[16px]"
                loading={processing}
                disabled={
                  viewOnly || userHasApproved || !userCanApprovePendingTier || approvalFlow.status !== 'pending'
                }
                onClick={async () => {
                  setProcessing(true);
                  await onApprove?.();
                  setProcessing(false);
                }}
              >
                {translate('common.approve')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="mt-10 border-t border-neutral-400">
        <OrderDetailsPage order={approvalFlow.order} showCtaButtons={false} showOrderStatusBar={false} />
      </div>
    </div>
  );
};

export default ApprovalFlowDetailsPage;
